import { useEffect, useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon, ExternalLinkIcon, PhoneIcon, QuoteIcon, StarIcon } from 'lucide-react';
import { business, googleReviewTeasers, googleReviewsExternalUrl, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Pegasus Spas Reviews | Customer Stories in Brooklyn, MI';
const DESCRIPTION = 'Read Google reviews and customer stories about Pegasus Spas, a family-owned hot tub, outdoor-living and spa-service showroom in Brooklyn, Michigan.';
const CANONICAL = 'https://www.pegasus-spas.com/testimonials.html';
type ArchivedQuote = {
  quote: string;
  attribution: string;
};
const archivedQuotes: ArchivedQuote[] = [{
  quote: 'I just wanted to reiterate the great customer service I experienced with Pegasus. I dealt with another Sundance dealer that was closer to my house, but they lacked product knowledge and had no interest in servicing a customer who clearly wanted to spend money with them. I returned to Pegasus who once again has excellent product knowledge of my spa, its warranty, and truly cares about their customers. Thank you for going the extra mile, once again.',
  attribution: "Lori O'Keefe"
}, {
  quote: 'Just a note to tell you that weather like this helps one appreciate a working spa! Running great, thanks for a job well done. Warm regards.',
  attribution: 'Jeff Kuhman, President, Glycon Corp.'
}, {
  quote: 'Would like to thank you folks again for the professionalism displayed by your service rep on a call at our home. (Dead mouse in control box.) You folks have a very good reputation and will be highly recommended by me to all the people we know. So many businesses put customer service as a low priority that it was a pleasure to see the positive attitude your company displays. Thanks again.',
  attribution: 'Sherry & Lara Robinson, Adrian, MI'
}, {
  quote: 'This is just a note to say thanks for your exceptional service. It is a pleasure to work with you.',
  attribution: 'Mike Guerin, Brooklyn, MI'
}, {
  quote: 'I would highly recommend purchasing a spa from Pegasus because of your phenomenal customer service! Thanks again and I look forward to being serviced by Pegasus for many years to come!',
  attribution: 'Tom Kanalas, Marketing Director of Wolf Creek Productions, Rives Junction, MI'
}, {
  quote: 'We use our spa everyday and sometimes twice a day for relaxation, therapy, entertainment and family time together. It is very comfortable and the model we have chosen, the Marin, is just the perfect size. We had some problems with the water at first, because of all the people in and out of the spa, but it got quickly corrected—thanks to your wonderful, helpful, courteous staff! Thank you!',
  attribution: 'Davison Family, Jackson, MI'
}, {
  quote: "The two gentlemen who delivered and installed the spa were excellent, pleasant and very knowledgeable. I've called your store and you responded immediately and your installers came out the same morning. Of course there was nothing wrong with my spa, only my being insecure. Your staff working in the store also was most helpful to me over the phone (the ladies were great too!). The spa was ordered and delivered exactly as promised. Thanks again. P.S. I love the Rubber Duck!",
  attribution: 'Herr Residence, Jackson, MI'
}, {
  quote: "We just love our spa. Its minimal maintenance and minimal sound are two important features to me. We did not shop for any special spa brands but checked out two dealers in Ann Arbor who did not have your experience or reputation! The only problem I have had is I can't sleep in it—my wife won't let me!",
  attribution: 'Pindzia Residence, Grass Lake, MI'
}, {
  quote: 'Using it in the dead of winter, with the snow falling and the steam just rolling out of it—how cool it is, with a cold brew and the sky above, to sit and relax.',
  attribution: 'Gor Household, Parma, MI'
}];
const initialArchive = archivedQuotes.slice(0, 4);
const remainingArchive = archivedQuotes.slice(4);
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function ArchiveQuote({
  item
}: {
  item: ArchivedQuote;
}) {
  return <figure className="border-t border-ink/15 pt-4" data-id="element-1318" data-mp="1hdbgjl8lorgq">
      <p className="text-[12px] font-bold uppercase text-ink/55" data-id="element-1319" data-mp="1m52vszmb3ark">Archived customer testimonial</p>
      <blockquote className="mt-2" data-id="element-1320" data-mp="1r4fr88r8tq5r">
        <p className="text-[16.5px] leading-7 text-ink/85" data-id="element-1321" data-mp="1r3e8di3zjzdh">“{item.quote}”</p>
      </blockquote>
      <figcaption className="mt-2 text-[15px] leading-6 text-ink/65" data-id="element-1322" data-mp="1w53hutbk84zg">
        — <cite className="not-italic" data-id="element-1323" data-mp="204vvhkft91bo">{item.attribution}</cite>
      </figcaption>
    </figure>;
}
export function GoogleReviewsPage() {
  const [archiveOpen, setArchiveOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1324" data-mp="37gejazfz784">
      {/* Compact interior hero */}
      <section aria-labelledby="reviews-h1" className="w-full bg-water" data-id="element-1325" data-mp="3ulm29614jii4">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1326" data-mp="3ujj41yptij2b">
          <div className="max-w-3xl" data-id="element-1327" data-mp="3plzqz24uwcby">
            <QuoteIcon className="h-8 w-8 text-pegasus-blue/70" aria-hidden="true" data-id="element-1328" data-mp="qwiv0ficau11" />
            <p className="mt-3 text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1329" data-mp="qyc21vwv7g9k">
              Customer Reviews
            </p>
            <h1 id="reviews-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1330" data-mp="2gd4m2dakkkib">
              What Local Customers Say About Pegasus Spas
            </h1>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1331" data-mp="26n1rw9rpp0ie">
              Choosing a hot tub or trusting someone to service it is a personal decision. These
              comments reflect the product guidance, installation help and long-term support
              our customers have experienced with us.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1332" data-mp="21mdun6cw3nfq">
              <a href={googleReviewsExternalUrl} target="_blank" rel="noopener noreferrer" aria-label="Read Pegasus Spas reviews on Google (opens in a new tab)" className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1333" data-mp="1nqjupcu3g8er">
                Read Reviews on Google
                <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1334" data-mp="3s8i0fzvgaygr" />
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1335" data-mp="9tqy4pv2w7ke">
                Visit Our Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Current Google reviews */}
      <section aria-labelledby="google-heading" className="w-full bg-white" data-id="element-1336" data-mp="opoqdx1vsrrc">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1337" data-mp="oodjhod533w0">
          <h2 id="google-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1338" data-mp="2ujg9uu0hjd8v">
            Customer Feedback on Google
          </h2>

          <div className="mt-8 grid max-w-4xl grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2" data-id="element-1339" data-mp="3f29hpmi7y2gc">
            {googleReviewTeasers.map(review => <figure key={review.name} className="border-t border-ink/15 pt-4" data-id="element-1340" data-mp="3ort0yt9ufybq">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-1341" data-mp="3stwo4f5vkrcu">
                  <p className="text-[12px] font-bold uppercase text-ink/55" data-id="element-1342" data-mp="3sw0poqgaxe9y">Google review</p>
                  <span className="flex items-center gap-0.5" role="img" aria-label="Five out of five stars" data-id="element-1343" data-mp="2w5qetey0das8">
                    {[0, 1, 2, 3, 4].map(star => <StarIcon key={star} className="h-3.5 w-3.5 fill-ember text-ember" aria-hidden="true" data-id="element-1344" data-mp="3a3y91f3ntxzp" />)}
                  </span>
                </div>
                <blockquote className="mt-2.5" data-id="element-1345" data-mp="pf0mo419btt2">
                  <p className="text-[17px] leading-7 text-ink" data-id="element-1346" data-mp="zg3420971lup">“{review.excerpt}”</p>
                </blockquote>
                <figcaption className="mt-2 text-[15px] leading-6 text-ink/65" data-id="element-1347" data-mp="uil2ww1f4ax9">
                  <cite className="not-italic" data-id="element-1348" data-mp="pq9qwutekyoc">{review.name}</cite>
                </figcaption>
              </figure>)}
          </div>

          <a href={googleReviewsExternalUrl} target="_blank" rel="noopener noreferrer" aria-label="Read all Pegasus Spas reviews on Google (opens in a new tab)" className="mt-8 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1349" data-mp="lnww31g72l6c">
            Read all Pegasus Spas reviews on Google
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1350" data-mp="1x4msxtyqa0na" />
          </a>
        </div>
      </section>

      {/* Archived customer stories */}
      <section aria-labelledby="archive-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1351" data-mp="3ib9hij6fp6hn">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1352" data-mp="3s7clzvordh7r">
          <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1353" data-mp="3s58e72nqny27">
            From the Pegasus Archives
          </p>
          <h2 id="archive-heading" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1354" data-mp="2zidvobahf8rd">
            Customer Letters and Testimonials
          </h2>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1355" data-mp="39906ztb61d49">
            Before online reviews became the norm, customers shared their experiences in letters and
            notes. The following comments are preserved from the Pegasus Spas testimonial archive.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2" data-id="element-1356" data-mp="2bu4i1wnf9rbb">
            {initialArchive.map(item => <ArchiveQuote key={item.attribution} item={item} data-id="element-1357" data-mp="r9qzitevwvs8" />)}
          </div>

          <button type="button" aria-expanded={archiveOpen} aria-controls="archive-more" onClick={() => setArchiveOpen(open => !open)} className="mt-9 inline-flex min-h-[48px] items-center gap-2 rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1358" data-mp="w2sh596h79fi">
            {archiveOpen ? 'Hide archived stories' : 'Read more archived stories'}
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-out ${archiveOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1359" data-mp="110bw6bazzq8i" />
          </button>

          <div id="archive-more" hidden={!archiveOpen} className={archiveOpen ? 'mt-8 grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2' : 'hidden'} data-id="element-1360" data-mp="3ltp2lhxl5oda">
            {remainingArchive.map(item => <ArchiveQuote key={item.attribution} item={item} data-id="element-1361" data-mp="1rc5i2bewbhf7" />)}
          </div>
        </div>
      </section>

      {/* Trust context */}
      <section aria-labelledby="trust-heading" className="w-full bg-water" data-id="element-1362" data-mp="1mi1c9vhd9ffp">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1363" data-mp="1iik42uak246u">
          <h2 id="trust-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1364" data-mp="1dkimkdss2cxh">
            Local Relationships Since 1977
          </h2>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-1365" data-mp="13m1rp5qpxe65">
            We have grown through repeat customers, referrals and generations of local
            relationships. Customer experiences naturally vary, but the goal remains the same: clear
            guidance, respectful help and support after the sale.
          </p>

          <ul className="mt-6 flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1366" data-mp="fq7yjg5xbvfr">
            {[{
            label: 'Why Pegasus',
            href: routes.about
          }, {
            label: 'Referral Program',
            href: routes.referrals
          }, {
            label: 'Service Work Guarantee',
            href: routes.serviceGuarantee
          }].map(link => <li key={link.label} data-id="element-1367" data-mp="tgtfgzbthvb0">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1368" data-mp="3swzu6r9ibyyh">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1369" data-mp="aoqwvifm84y4" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="conversation-heading" className="w-full bg-ink" data-id="element-1370" data-mp="1m83fpflse2gt">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1371" data-mp="1kpd27zwlk2v0">
          <div className="max-w-3xl" data-id="element-1372" data-mp="1frbjxh6zw7kl">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-1373" data-mp="1fscvrnvu4y3d">
              Experience It for Yourself
            </p>
            <h2 id="conversation-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1374" data-mp="1pny6qbivt8p9">
              Start With a Conversation
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1375" data-mp="1ynulnlfgyt74">
              Visit the Brooklyn showroom, compare options in person or call with a question.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1376" data-mp="28fqqrgfz3jku">
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1377" data-mp="2me73pp4fi6bf">
              Visit Our Showroom
            </SiteLink>
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1378" data-mp="35yxngf7ueouv">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1379" data-mp="2i9waqfrz8rjs" />
              Call or Text {business.phone}
            </a>
          </div>
        </div>
      </section>
    </main>;
}